import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Truck, Award, RefreshCcw } from "lucide-react";
import ProductCard from "../components/ProductCard";
import SocialProofBar from "../components/SocialProofBar";
import UGCSection from "../components/UGCSection";
import { useCatalog, useBanners } from "../lib/catalog";
import { useSiteSettings } from "../lib/hooks";

export default function Home() {
  const { products, categories, loading } = useCatalog();
  const heroBanners = useBanners("hero");
  const promoBanners = useBanners("promo");
  const settings = useSiteSettings();
  const hero = heroBanners[0];
  const promo = promoBanners[0];

  const bestsellers = products
    .filter((p) => p.badge === "Bestseller" || p.badge === "Top Rated")
    .slice(0, 4);
  const featured = products.slice(0, 8);

  const categoryImg = (slug) => {
    if (slug === "card-holders") return "https://m.media-amazon.com/images/I/51TkG18Sx4L._SL1100_.jpg";
    if (slug === "wallets") return "https://m.media-amazon.com/images/I/718L9cT17oL._SL1100_.jpg";
    if (slug === "keychains") return "https://m.media-amazon.com/images/I/51k-GoLeASL._SL1113_.jpg";
    const p = products.find((x) => x.category === slug);
    return p?.images?.[0] || "";
  };

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="bg-neutral-50 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-20 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            {hero?.eyebrow && (
              <span className="inline-block bg-[#FF5A1F]/10 text-[#FF5A1F] text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
                {hero.eyebrow}
              </span>
            )}
            <h1 className="mt-4 md:mt-5 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[0.98] md:leading-[0.95] text-neutral-900">
              {hero ? hero.title : "Carry smarter. Live lighter."}
            </h1>
            {hero?.subtitle && (
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-neutral-600 max-w-md leading-relaxed">{hero.subtitle}</p>
            )}
            <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
              <Link to={hero?.cta_link || "/shop"} className="h-11 md:h-12 px-6 md:px-7 rounded-full bg-neutral-900 hover:bg-[#FF5A1F] text-white font-semibold text-sm flex items-center gap-2 transition-colors" data-testid="hero-shop-btn">
                {hero?.cta_text || "Shop the Collection"} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop/keychains" className="h-11 md:h-12 px-6 md:px-7 rounded-full border border-neutral-900 hover:bg-neutral-900 hover:text-white font-semibold text-sm flex items-center transition-colors">
                Explore Keychains
              </Link>
            </div>
            <div className="mt-7 md:mt-10 flex flex-wrap gap-x-5 gap-y-2 md:gap-8 text-xs md:text-sm text-neutral-700">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#FF5A1F]" /> RFID Protected</div>
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#FF5A1F]" /> Free Delivery</div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#FF5A1F]" /> 6-mo Warranty</div>
            </div>
          </div>
          {/* Lifestyle hero */}
          <div className="relative h-[300px] sm:h-[380px] md:h-[560px] rounded-3xl overflow-hidden bg-neutral-200">
            <img
              src={settings?.hero_lifestyle_image || "https://images.unsplash.com/photo-1627123424574-724758594e93?w=1600&auto=format&fit=crop&q=80"}
              alt="Stealodeal lifestyle"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-neutral-900/40 via-transparent to-transparent" />
            {/* floating product card overlay */}
            <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 w-28 sm:w-36 md:w-52 aspect-[4/5] bg-white rounded-2xl shadow-2xl overflow-hidden">
              <img src={hero?.image || "https://m.media-amazon.com/images/I/51TkG18Sx4L._SL1100_.jpg"} alt="" className="w-full h-full object-contain p-4" />
            </div>
          </div>
        </div>
      </section>

      {/* Social proof strip */}
      <SocialProofBar />

      {/* Categories */}
      <section className="py-12 md:py-20" data-testid="category-strip">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-6 md:mb-8 gap-3">
            <div>
              <h2 className="font-display text-2xl md:text-4xl font-black leading-tight">Shop by Category</h2>
              <p className="text-neutral-500 mt-1 text-sm md:text-base">Find your everyday essential</p>
            </div>
            <Link to="/shop" className="text-xs md:text-sm font-semibold hover:text-[#FF5A1F] whitespace-nowrap">View all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {categories.map((c) => (
              <Link to={`/shop/${c.slug}`} key={c.slug} className="group relative aspect-[4/3] rounded-2xl md:rounded-3xl overflow-hidden bg-neutral-100" data-testid={`category-${c.slug}`}>
                <img src={c.image || categoryImg(c.slug)} alt={c.name} className="w-full h-full object-contain p-6 md:p-10 transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 via-neutral-900/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6 text-white">
                  <div className="text-[9px] md:text-xs uppercase tracking-wider md:tracking-widest opacity-80 line-clamp-1">{c.blurb}</div>
                  <div className="mt-0.5 md:mt-1 flex items-center justify-between gap-2">
                    <h3 className="font-display text-base md:text-3xl font-black leading-tight line-clamp-1">{c.name}</h3>
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 opacity-0 -translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition flex-shrink-0" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers */}
      {bestsellers.length > 0 && (
        <section className="py-12 md:py-16 bg-neutral-50" data-testid="bestsellers-section">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-6 md:mb-8 gap-3">
              <div>
                <span className="text-[10px] font-bold tracking-[0.24em] uppercase text-[#FF5A1F]">Community Favourites</span>
                <h2 className="font-display text-2xl md:text-4xl font-black mt-1 leading-tight">Bestsellers</h2>
              </div>
              <Link to="/shop" className="text-xs md:text-sm font-semibold hover:text-[#FF5A1F] whitespace-nowrap">View all →</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-7">
              {bestsellers.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Promo banner */}
      {promo && (
        <section className="py-12 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="relative rounded-3xl md:rounded-[32px] bg-neutral-900 text-white overflow-hidden p-6 md:p-16 grid md:grid-cols-2 gap-6 md:gap-8 items-center">
              <div>
                {promo.eyebrow && <div className="text-[10px] font-bold tracking-[0.24em] uppercase text-[#FF5A1F]">{promo.eyebrow}</div>}
                <h2 className="font-display text-2xl md:text-5xl font-black mt-3 leading-tight">{promo.title}</h2>
                {promo.subtitle && <p className="mt-3 md:mt-4 text-sm md:text-base text-neutral-300 max-w-md">{promo.subtitle}</p>}
                {promo.cta_text && (
                  <Link to={promo.cta_link || "/shop"} className="mt-6 md:mt-8 inline-flex items-center gap-2 h-11 md:h-12 px-6 md:px-7 rounded-full bg-[#FF5A1F] hover:bg-white hover:text-neutral-900 font-semibold text-sm transition" data-testid="promo-cta">
                    {promo.cta_text} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
              {promo.image && (
                <div className="relative h-48 md:h-80">
                  <img src={promo.image} alt="promo" className="absolute inset-0 w-full h-full object-contain" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="py-12 md:py-16" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-6 md:mb-8 gap-3">
            <h2 className="font-display text-2xl md:text-4xl font-black leading-tight">Fresh Arrivals</h2>
            <Link to="/shop" className="text-xs md:text-sm font-semibold hover:text-[#FF5A1F] whitespace-nowrap">See all →</Link>
          </div>
          {loading ? (
            <div className="py-10 text-neutral-500">Loading...</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-7">
              {featured.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* UGC — Instagram feed */}
      <UGCSection />

      {/* Perks */}
      <section className="py-12 md:py-16 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {[
            { icon: Shield, t: "RFID Protected", d: "Shield your cards from digital theft." },
            { icon: Truck, t: "Fast Delivery", d: "Free shipping on orders above ₹499." },
            { icon: RefreshCcw, t: "Easy Returns", d: "10-day hassle-free returns." },
            { icon: Award, t: "6-mo Warranty", d: "Trusted by 10,000+ customers." },
          ].map((f) => (
            <div key={f.t} className="text-left">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#FF5A1F]/10 flex items-center justify-center text-[#FF5A1F]">
                <f.icon className="w-4 h-4 md:w-5 md:h-5" />
              </div>
              <h4 className="mt-3 md:mt-4 font-semibold text-sm md:text-base text-neutral-900">{f.t}</h4>
              <p className="text-xs md:text-sm text-neutral-500 mt-1">{f.d}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
